import type { ReactNode } from "react";
import { renderMarkdownContent } from "@/lib/markdown-renderer";
import { AnimatedImage } from "@/components/ui/animated-image";
import {
  CalendarEventCard,
  type CalendarEventData,
} from "@/components/terminal/cards/CalendarEventCard";
import {
  EmailCard,
  type EmailData,
} from "@/components/terminal/cards/EmailCard";
import { TerminalOutputCard } from "@/components/terminal/cards/TerminalOutputCard";
import { TerminalSnapshotCard } from "@/components/terminal/cards/TerminalSnapshotCard";
import { CommandHistoryCard } from "@/components/terminal/cards/CommandHistoryCard";
import type { CommandHistoryEntry } from "@/components/terminal/command-tracker";
import { RunCommandCard } from "@/components/messages/RunCommandCard";
import { SyncEventCard } from "@/components/sync/SyncEventCard";
import { extractSyncEvent } from "@ano/shared/sync/sync-event";

// Phase 2+3 renderer: dispatches on `message.kind` and reads the typed
// `payload` object (no HTML-comment parsing). Legacy messages (pre-Phase
// 1) with kind='text' and embedded <!--card:--> markers render as text —
// acceptable per the master plan's no-users assumption for now.

type MessageForRender = {
  id?: string;
  content: string;
  kind?: string | null;
  payload?: Record<string, unknown> | null;
  canEdit?: boolean;
};

function renderCardForKind(
  kind: string,
  payload: Record<string, unknown> | null,
  messageId?: string,
  canEdit?: boolean,
): ReactNode | null {
  if (!payload) return null;
  switch (kind) {
    case "calendar":
      return (
        <CalendarEventCard
          event={payload as unknown as CalendarEventData}
          compact
        />
      );
    case "email":
      return <EmailCard email={payload as unknown as EmailData} compact />;
    case "terminal_output":
      return (
        <TerminalOutputCard
          content={payload.content as string}
          cwd={payload.cwd as string}
          compact
          messageId={messageId}
          canEdit={canEdit}
          payload={payload}
        />
      );
    case "terminal_snapshot":
      return (
        <TerminalSnapshotCard
          ansi={(payload.ansi as string) ?? ""}
          plain={(payload.plain as string) ?? ""}
          cwd={payload.cwd as string}
          timestamp={payload.timestamp as number}
          compact
        />
      );
    case "command_history":
      return (
        <CommandHistoryCard
          entry={payload as unknown as CommandHistoryEntry}
          compact
          onRerun={(command) => {
            window.dispatchEvent(
              new CustomEvent("terminal:rerun", { detail: { command } }),
            );
          }}
        />
      );
    case "run":
      return (
        <RunCommandCard
          command={(payload.command as string) ?? ""}
          output={(payload.output as string) ?? ""}
          exitCode={
            typeof payload.exit_code === "number"
              ? (payload.exit_code as number)
              : ((payload.exitCode as number) ?? 0)
          }
          compact
        />
      );
    case "gif": {
      const url = payload.url as string | undefined;
      if (!url) return null; // picker .gif uploads (no payload.url) fall through to AttachmentPreview
      const searchTerm = payload.search_term as string | undefined;
      return (
        <AnimatedImage
          src={url}
          alt={searchTerm ?? "GIF"}
          className="max-w-xs max-h-64 rounded"
        />
      );
    }
    // `terminal_live` and `terminal_recording` kinds remain in the
    // message-kind enum so historical rows don't FK-fail, but the
    // sharing pipeline that produced them was removed (the in-process
    // server `Map` couldn't survive multi-replica). Old rows render
    // as nothing via the default branch.
    default:
      return null;
  }
}

export function renderMessageContent(
  message: MessageForRender | string,
  workspaceMembers: { id: string; display_name: string }[],
  onUserClick?: (userId: string) => void,
  channels?: { id: string; name: string }[],
  onChannelClick?: (channelId: string) => void,
): ReactNode {
  // Back-compat: accept a bare content string. Treated as kind='text'.
  const msg: MessageForRender =
    typeof message === "string" ? { content: message, kind: "text" } : message;
  const kind = msg.kind ?? "text";
  const payload = msg.payload ?? null;

  // Ano Sync — a normal kind="text" message carrying a Sync Event under the
  // `ano_sync` payload marker renders as a structured card. Checked before
  // the kind dispatch (sync events are not a distinct message kind, so they'd
  // otherwise fall through to plain markdown). See packages/shared/src/sync.
  const syncEvent = extractSyncEvent(payload);
  if (syncEvent) return <SyncEventCard event={syncEvent} />;

  // Card kinds — dispatch on kind, ignore content string (it's the empty
  // caption for card-kind messages).
  const card = renderCardForKind(kind, payload, msg.id, msg.canEdit);
  if (card) return card;

  // Text-style kinds (including call/system which render as short system
  // messages handled outside this renderer) use the markdown pipeline.
  return renderMarkdownContent(
    msg.content,
    workspaceMembers,
    onUserClick,
    channels,
    onChannelClick,
  );
}
